import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { Subscription, forkJoin } from 'rxjs';
import { RoleManagementService } from '../../services/role-management.service';
import { UserModel } from '../../models/user';
import { RoleAssignmentModel } from '../../models/role-assignments';
import { RoleModel } from '../../models/role';
import { AddEvent, CancelEvent, GridComponent, RemoveEvent } from '@progress/kendo-angular-grid';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EventBusService } from '../../../../services/event-bus.service';

/**
 * Roles administration
 */
@Component({
  selector: 'admin-roles-management',
  templateUrl: './admin-roles-management.component.html',
  styleUrls: ['./admin-roles-management.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminRolesManagementComponent implements OnInit, OnDestroy {
  /**
   * The (list of) subscription(s)
   */
  subscriptions: Subscription = new Subscription();

  /**
* All users
*/
  public allUsers: UserModel[] = [];
  /**
* Filtered users
*/
  public filteredUsers: UserModel[] = [];
  /**
* List of assignments
*/
  public aasignments: RoleAssignmentModel[] = [];
  /**
*List of roles
*/
  public roles: RoleModel[] = []
  /**
* List of selected grid rows
*/
  public mySelection: number[] = [];
  /**
* Form group for adding/editing grid rows
*/
  public formGroup: UntypedFormGroup;
  /**
* Selected user id
*/
  public selectedUser: number;
  /**
* Edited row index
*/
  private editedRowIndex: number;
  /**
* Grids element
*/
  @ViewChildren('grid') grids: QueryList<GridComponent>;

  /**
* Flag if confirmation dialog should be displayed
*/
  displatConfirmationDialog = false;

  /**
* Selected role in the panel
*/
  selectedRole: RoleModel;

  /**
* Selected item for remove action
*/
  removedItem: number;

  /**
   * Loading flag
   */
  loading = false;

  /**
* Constructor
*/
  constructor(private ref: ChangeDetectorRef, private roleService: RoleManagementService, private eventBus: EventBusService) {
  }

  /**
 * Destroys the component
 */
  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
* On init
*/
  ngOnInit(): void {
    this.loadData();
  }

  /**
* Loads data
*/
  private loadData() {
    this.loading = true;
    this.subscriptions.add(forkJoin({
      assignments: this.roleService.getRoleAssignments(),
      roles: this.roleService.getRoleDetails(),
      users: this.roleService.getUsers()
    })
      .subscribe(({ assignments, roles, users }) => {
        this.allUsers = users;
        this.filteredUsers = users;
        this.aasignments = assignments;
        this.roles = roles;
        this.roles.forEach((r) => {
          r.assignments = this.aasignments.filter(a => a.roleId === r.oid);
        })
        this.loading = false;
        this.ref.markForCheck();
      }));
  }

  /**
  * Grid handler for adding new row
  */
  public addHandler(args: AddEvent): void {
    this.closeEditor(args.sender);
    // define all editable fields validators and default values
    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl("", Validators.required),
      oid: new UntypedFormControl("", Validators.required),

    });
    // show the new row editor, with the `FormGroup` build above
    args.sender.addRow(this.formGroup);
  }

  /**
* Close the editor for the given row
*/
  public cancelHandler(args: CancelEvent): void {
    this.closeEditor(args.sender, args.rowIndex);
  }

  /**
  * Add new assignemnt
  */
  public addAssignment(roleId: number, role: RoleModel): void {
    const selectedGrid: GridComponent = this.grids.toArray()[0];
    selectedGrid.data = role.assignments
    selectedGrid.closeRow();
    this.subscriptions.add(this.roleService.addAssignment(roleId, this.selectedUser).subscribe(result => {
      if (result.m_Item1) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            //Nothing
          }
        }, 'roleAssignmentApplyOnReload');
      }
      else {
        if (result.m_Item2 == "Duplicate entry!") {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'roleAssignmentDuplicateEntry');
        }
        else {
          //Nothing
        }
        this.loadData();
      }
    }));
  }

  /**
* Grid handler for removing data
*/
  public removeHandler(args: RemoveEvent, role: RoleModel): void {
    const dataItem = args.dataItem;
    this.selectedRole = role;
    this.removedItem = dataItem.oid;
    this.displatConfirmationDialog = true;
  }

  /**
   * Confirm removing
   * @param remove
   */
  public confirmRemove(remove: boolean) {
    if (remove) {
      this.subscriptions.add(this.roleService.deleteAssignment(this.removedItem).subscribe(data => {

        if (data.m_Item1) {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'roleAssignmentApplyOnReload');
        }
        const selectedGrid: GridComponent = this.grids.toArray()[0];
        this.selectedRole.assignments = this.selectedRole.assignments.filter(d => d.oid !== this.removedItem);
        selectedGrid.data = this.selectedRole.assignments;
        this.removedItem = null;
        this.selectedRole = null;
        this.displatConfirmationDialog = false;
        this.ref.markForCheck();
      }));
    } else {
      this.removedItem = null;
      this.selectedRole = null;
      this.displatConfirmationDialog = false;
    }
  }

  /**
* Filters users
*/
  handleFilter(filter: string) {
    this.filteredUsers = this.allUsers.filter(u => u.User_Description.includes(filter));
    this.ref.markForCheck();
  }

  /**
* Grid handler for closing editor
*/
  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
